'use client';

import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { DiscoveryFilters, DiscoverySortOption, LocationType, ActivityFilter } from '@/redux/slices/discoverySlice';
import './HorizontalFilterBar.scss';

interface PopularInterest {
  tag: string;
  userCount: number;
}

interface HorizontalFilterBarProps {
  filters: DiscoveryFilters;
  onFilterChange: (filters: Partial<DiscoveryFilters>) => void;
  onSearch: () => void;
}

const HorizontalFilterBar: React.FC<HorizontalFilterBarProps> = ({
  filters,
  onFilterChange,
  onSearch,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [popularInterests, setPopularInterests] = useState<PopularInterest[]>([]);
  const [interestDraft, setInterestDraft] = useState('');

  // Fetch the popular-interests list once the advanced panel opens, so the
  // initial discovery render doesn't pay for it.
  useEffect(() => {
    if (!showAdvanced || popularInterests.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ interests: PopularInterest[] }>(
          '/discovery/popular-interests'
        );
        if (!cancelled) setPopularInterests(data.interests);
      } catch {
        // Silent — the picker still works with manual-entry input.
      }
    })();
    return () => { cancelled = true; };
  }, [showAdvanced, popularInterests.length]);

  const handleToggleInterest = (tag: string) => {
    const normalized = tag.trim().toLowerCase();
    if (!normalized) return;
    const current = filters.interests || [];
    const next = current.includes(normalized)
      ? current.filter((t) => t !== normalized)
      : [...current, normalized];
    onFilterChange({ interests: next });
    onSearch();
  };

  const handleInterestDraftKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && interestDraft.trim()) {
      e.preventDefault();
      handleToggleInterest(interestDraft);
      setInterestDraft('');
    }
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const radius = parseInt(e.target.value);
    onFilterChange({ radius });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ search: e.target.value });
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ sort: e.target.value as DiscoverySortOption });
    onSearch();
  };

  const handleLocationTypeChange = (type: LocationType) => {
    onFilterChange({ locationType: type });
    onSearch();
  };

  const handleActivityChange = (activity: ActivityFilter) => {
    onFilterChange({ activity: activity === filters.activity ? null : activity });
    onSearch();
  };

  const handleOwnershipChange = (key: 'iOwn' | 'iKlyxd') => {
    onFilterChange({ [key]: !filters[key] });
    onSearch();
  };

  const handleReset = () => {
    onFilterChange({
      radius: 250,
      search: '',
      sort: 'nearest',
      locationType: 'both',
      activity: null,
      badges: [],
      iOwn: false,
      iKlyxd: false,
      interests: [],
    });
    onSearch();
  };

  return (
    <div className="horizontal-filter-bar">
      {/* Main Filter Row */}
      <div className="filter-row filter-row-main">
        {/* Distance Slider */}
        <div className="filter-group filter-distance">
          <label>
            Distance: {filters.radius} {filters.unit}
          </label>
          <input
            type="range"
            min="10"
            max="250"
            step="5"
            value={filters.radius}
            onChange={handleRadiusChange}
            onMouseUp={onSearch}
            onTouchEnd={onSearch}
            className="distance-slider"
          />
        </div>

        {/* Text Search */}
        <div className="filter-group filter-search">
          <input
            type="text"
            placeholder="Search by name..."
            value={filters.search}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            className="search-input"
          />
          <button
            type="button"
            onClick={onSearch}
            className="search-button"
            aria-label="Search"
          >
            Search
          </button>
        </div>

        {/* Sort Dropdown */}
        <div className="filter-group filter-sort">
          <select
            value={filters.sort}
            onChange={handleSortChange}
            className="sort-select"
          >
            <option value="nearest">Nearest</option>
            <option value="most-klyxd">Most Klyxd</option>
            <option value="recently-active">Recently Active</option>
            <option value="newest">Newest</option>
            <option value="highest-value">Highest Value</option>
          </select>
        </div>

        {/* Location Type Toggles */}
        <div className="filter-group filter-location-type">
          <button
            className={`location-btn ${filters.locationType === 'home' || filters.locationType === 'both' ? 'active' : ''}`}
            onClick={() => handleLocationTypeChange(filters.locationType === 'home' ? 'both' : 'home')}
          >
            Home
          </button>
          <button
            className={`location-btn ${filters.locationType === 'touring' || filters.locationType === 'both' ? 'active' : ''}`}
            onClick={() => handleLocationTypeChange(filters.locationType === 'touring' ? 'both' : 'touring')}
          >
            Touring
          </button>
        </div>

        {/* Advanced Toggle */}
        <button
          className="filter-toggle-btn"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Less' : 'More'} Filters
        </button>

        {/* Reset Button */}
        <button
          className="filter-reset-btn"
          onClick={handleReset}
        >
          Reset
        </button>
      </div>

      {/* Advanced Filter Row */}
      {showAdvanced && (
        <div className="filter-row filter-row-advanced">
          {/* Activity Filters */}
          <div className="filter-group filter-activity">
            <label>Activity:</label>
            <div className="filter-pills">
              <button
                className={`filter-pill ${filters.activity === '24h' ? 'active' : ''}`}
                onClick={() => handleActivityChange('24h')}
              >
                24h
              </button>
              <button
                className={`filter-pill ${filters.activity === '7d' ? 'active' : ''}`}
                onClick={() => handleActivityChange('7d')}
              >
                7 days
              </button>
              <button
                className={`filter-pill ${filters.activity === '30d' ? 'active' : ''}`}
                onClick={() => handleActivityChange('30d')}
              >
                30 days
              </button>
            </div>
          </div>

          {/* Ownership Filters */}
          <div className="filter-group filter-ownership">
            <label>My Profiles:</label>
            <div className="filter-pills">
              <button
                className={`filter-pill ${filters.iOwn ? 'active' : ''}`}
                onClick={() => handleOwnershipChange('iOwn')}
              >
                I Own
              </button>
              <button
                className={`filter-pill ${filters.iKlyxd ? 'active' : ''}`}
                onClick={() => handleOwnershipChange('iKlyxd')}
              >
                I Klyxd
              </button>
            </div>
          </div>

          {/* Interests Filter */}
          <div className="filter-group filter-interests">
            <label>Interests:</label>
            <div className="filter-pills">
              {(filters.interests || []).map((tag) => (
                <button
                  key={`selected-${tag}`}
                  className="filter-pill active"
                  onClick={() => handleToggleInterest(tag)}
                  aria-label={`Remove ${tag} filter`}
                >
                  #{tag} ×
                </button>
              ))}
              {popularInterests
                .filter((p) => !(filters.interests || []).includes(p.tag))
                .slice(0, 12)
                .map((p) => (
                  <button
                    key={`popular-${p.tag}`}
                    className="filter-pill"
                    onClick={() => handleToggleInterest(p.tag)}
                    title={`${p.userCount} profile${p.userCount === 1 ? '' : 's'}`}
                  >
                    #{p.tag}
                  </button>
                ))}
              <input
                type="text"
                className="filter-interest-input"
                placeholder="Add interest…"
                value={interestDraft}
                onChange={(e) => setInterestDraft(e.target.value)}
                onKeyDown={handleInterestDraftKey}
                maxLength={32}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HorizontalFilterBar;
